import { twJoin } from 'tailwind-merge';

import MarketoForm from 'molecules/Form';
import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Image from 'molecules/Image';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';
import VideoCustom from 'molecules/VideoCustom';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { LandingPageHeroProps } from 'components/LandingPageHero/config';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';

import type { IconIds } from 'molecules/Icon/types';
import type { FC } from 'react';

const Meta: FC<{ icon: IconIds; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-3 text-md text-gray-300">
    <Icon icon={icon} size={22} />
    <span className="whitespace-nowrap">{label}</span>
  </div>
);

const LandingPageHero: FC<LandingPageHeroProps> = props => {
  const {
    sectionProps,
    rest: { heading, readTime, mediaType, image, video, form, fullWidthMedia, secondaryCta },
  } = splitSectionProps(props);

  const hasPreloadedForm = !!form?.value?.data;
  const formRefData = useBuilderReference(
    !hasPreloadedForm ? form?.model : undefined,
    !hasPreloadedForm ? form?.id : undefined,
  );
  const formData = hasPreloadedForm ? form.value.data : formRefData;

  // Below lg the Marketo form mounts post-hydration, so its space is reserved to avoid CLS. A single-input
  // form is only an email field + button (108px stacked below sm, 48px inline above), so reserving the full
  // 520px meant for a multi-field form left a large empty gap under it.
  const isSingleInputForm = (formData as FormReference | undefined)?.variant === 'singleInput';
  const formWrapperClass = twJoin('flex flex-col gap-3', !isSingleInputForm && 'min-h-[520px] lg:min-h-0');
  const formClass = isSingleInputForm ? 'min-h-[108px] sm:min-h-[48px] lg:min-h-0' : undefined;

  const hasSecondaryCta = Boolean(secondaryCta?.text && secondaryCta?.url);

  const renderSecondaryCta = () => {
    if (!hasSecondaryCta) {
      return null;
    }

    return (
      <ScrollFadeSection delay={0.35} animateOnMount>
        <div className="flex items-center justify-start gap-1">
          <a
            href={secondaryCta?.url || '#'}
            target={secondaryCta?.openInNewTab ? '_blank' : '_self'}
            rel={secondaryCta?.openInNewTab ? 'noopener noreferrer' : undefined}
            className="group flex items-center gap-1.5 text-lg font-medium text-gray-300 transition-colors hover:text-gray-400"
          >
            <span>{secondaryCta?.text || 'Lorem ipsum'}</span>
            <Icon icon="arrow-up-right" size={20} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </ScrollFadeSection>
    );
  };

  if (fullWidthMedia) {
    return (
      <Section {...sectionProps} bare paddingTop="none" sidePadding="none">
        <div className="relative overflow-hidden sm:min-h-[529px] xl:min-h-[900px]">
          <div className="relative z-10 px-4 pt-6.5 sm:px-16 sm:pt-12 lg:px-8 lg:pt-14">
            <div className="mx-auto max-w-screen-xl xl:px-8">
              <div className="flex max-w-lg flex-col gap-6">
                {heading && (
                  <Heading
                    eyebrow={heading.eyebrow}
                    eyebrowWords={heading.eyebrowWords}
                    heading={heading.heading}
                    subheading={heading.subheading}
                    body={heading.body}
                    buttons={heading.buttons}
                    as={heading.as || 'h2'}
                    alignment="left"
                    size="lg"
                    animateOnMount
                  />
                )}
                {readTime && (
                  <div className="flex items-center gap-2">
                    <Meta icon="book-open-01" label={`${readTime} min read`} />
                  </div>
                )}
                {formData && (
                  <div className={formWrapperClass}>
                    <div className={formClass}>
                      <MarketoForm formData={formData as FormReference} />
                    </div>
                    {renderSecondaryCta()}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="relative mt-8 lg:absolute lg:-right-10 lg:top-0 lg:mt-0 lg:w-[50vw] lg:px-0 xl:right-0 ">
            {mediaType === 'image' && image && (
              <Image
                {...image.file}
                className="size-full object-cover object-[65%_center]"
                alt={image.alt}
                url={image.file?.url}
                priority
              />
            )}
            {mediaType === 'video' && video && (
              <div className="size-full">
                <VideoCustom {...video} key={video.videoSrc} priority />
              </div>
            )}
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section {...sectionProps}>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
        <div className="flex max-w-lg flex-col gap-6">
          {heading && <Heading {...heading} as={heading.as || 'h1'} alignment="left" size="lg" animateOnMount />}
          {readTime && (
            <div className="flex items-center gap-2">
              <Meta icon="book-open-01" label={`${readTime} min read`} />
            </div>
          )}
          {formData && (
            <div className={formWrapperClass}>
              <div className={formClass}>
                <MarketoForm formData={formData as FormReference} />
              </div>
              {renderSecondaryCta()}
            </div>
          )}
        </div>

        <div className="flex justify-center">
          {mediaType === 'image' && image && (
            <Image
              {...image.file}
              className="w-full max-w-lg"
              alt={image.alt}
              url={image.file?.url}
              priority
              maxWidth={768}
            />
          )}
          {mediaType === 'video' && video && (
            <div className="size-full">
              <VideoCustom {...video} key={video.videoSrc} priority />
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default LandingPageHero;
